import React, {useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import {initial} from '../../../common/domain/RemoteData.model';
import {remoteDataWithSetter} from '../../../common/utils/remoteDataWithSetter';
import {CatalogItemCard} from '../../../common/ui/CatalogItemCard';
import {RemoteDataUI} from '../../../common/ui/RemoteDataUI';
import {Spinner} from 'react-bootstrap';
import {Indicador} from '../../../ui';
import {fetchTracked} from "../../catalog/infrastructure/CatalogService";
import {PriceWithFallback} from "../../../common/ui/PriceWithFallback";

/**
 * UI constants
 */
const COLORS = {
    primaryText: '#007bff',
    lightAlertBackground: '#f8f9fa',
    dangerAlertBackground: '#f8d7da',
};

const SPACING = {
    sectionPadding: '1.5rem',
    cardGap: '1.5rem',
};

/**
 * Styled components
 */
const Container = styled.div`
    display: flex;
    gap: ${SPACING.sectionPadding};
    flex-direction: column;
`;

const ScrollArea = styled.div`
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 0.25rem;
`;

const GridWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${SPACING.cardGap};
    box-sizing: border-box;
`;

const CardWrapper = styled.div`
    flex: 0 0 auto;
`;

const Alert = styled.div`
    background-color: ${({ type }) =>
            type === 'light' ? COLORS.lightAlertBackground : COLORS.dangerAlertBackground};
    padding: ${SPACING.cardGap};
    border-radius: 0.5rem;
    font-size: 1rem;
`;

const CenteredSpinner = styled.div`
    display: flex;
    justify-content: center;
    margin: 2rem 0;
`;

/**
 * TrackingProductsOverview - Displays a scrollable, wrapped grid of tracked products
 * @returns {JSX.Element}
 */
export const TrackingProductsOverview = () => {
    const [trackedProductsData, setTrackedProductsData] = useState(initial([]));

    useEffect(() => {
        remoteDataWithSetter(setTrackedProductsData, fetchTracked);
    }, []);

    const renderTrackedProduct = useCallback((product, index) => (
        <CardWrapper key={index}>
            <CatalogItemCard data={{ catalogItem: product }}>
                <CatalogItemCard.Price>
                    <PriceWithFallback data={{sellingPrice: product.price?.bestPrice, basePrice: product.price?.value}}/>
                </CatalogItemCard.Price>
            </CatalogItemCard>
        </CardWrapper>
    ), []);

    return (
        <RemoteDataUI remoteData={trackedProductsData}>
            <RemoteDataUI.Success>
                {(products) =>
                    products.length === 0 ? null : (
                        <Container>
                            <Indicador indicador="Productos pendientes" />
                            <ScrollArea>
                                <GridWrapper>
                                    {products.map(renderTrackedProduct)}
                                </GridWrapper>
                            </ScrollArea>
                        </Container>
                    )
                }
            </RemoteDataUI.Success>
                <RemoteDataUI.Pending>
                    <CenteredSpinner>
                        <Spinner />
                    </CenteredSpinner>
                </RemoteDataUI.Pending>
                <RemoteDataUI.Failure>
                    <Alert type="danger">Imposible cargar productos.</Alert>
                </RemoteDataUI.Failure>
        </RemoteDataUI>
    );
};
